import React from "react";
import { useMutation, useQuery } from "@apollo/client";
import { gql } from "@apollo/client";
import { Product } from "@syos-music/products";

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  IconButton,
  Fab,
  Switch,
  FormControl,
  FormControlLabel,
  Menu,
  MenuItem,
  CircularProgress,
  Container,
  Stack,
} from "@mui/material";
import {
  Plus as CreateIcon,
  DotsVertical as MenuIcon,
  Cancel as DisabledIcon,
} from "mdi-material-ui";

import Layout from "components/common/Layout";
import CreateProductDialog from "components/product/CreateProductDialog";

const MUTATION = gql`
  mutation ($productId: ID!, $disabled: Boolean!) {
    UpdateProduct(id: $productId, disabled: $disabled) {
      product {
        id
        disabled
      }
    }
  }
`;

function ProductTableRow({ product, onUpdate }) {
  const menuRef = React.useRef();
  const [menuOpen, setMenuOpen] = React.useState(false);

  const [updateProduct, { loading }] = useMutation(MUTATION);

  return (
    <TableRow
      sx={{
        "&:nth-of-type(odd)": { bgcolor: (t) => t.palette.background.odd },
        "&:nth-of-type(even)": { bgcolor: (t) => t.palette.background.even },
      }}
    >
      <TableCell>
        <Typography>{product.label?.fr}</Typography>
        <Typography color="textSecondary">{product.name}</Typography>
      </TableCell>
      <TableCell align="right">
        <Product.Price product={product} currency="EUR" />
        <br />
        <Product.Price product={product} currency="USD" />
      </TableCell>
      <TableCell align="right">{product.account}</TableCell>

      <TableCell align="center" padding="checkbox">
        {product.disabled && <DisabledIcon />}
      </TableCell>
      <TableCell padding="checkbox">
        <IconButton
          size="small"
          ref={menuRef}
          onClick={() => setMenuOpen(true)}
        >
          <MenuIcon />
        </IconButton>
        <Menu
          anchorEl={menuRef.current}
          keepMounted
          open={menuOpen}
          onClose={() => setMenuOpen(false)}
        >
          <MenuItem
            disabled={loading}
            onClick={async () => {
              updateProduct({
                variables: {
                  productId: product.id,
                  disabled: !product.disabled,
                },
              }).then(() => {
                setMenuOpen(false);
                return onUpdate?.();
              });
            }}
          >
            {product.disabled ? "Ré-activer" : "Désactiver"}
          </MenuItem>
        </Menu>
      </TableCell>
    </TableRow>
  );
}

const productsQuery = gql`
  query ($disabled: Boolean) {
    products(disabled: $disabled) {
      id
      name
      disabled
      label {
        fr
      }
      account

      ...Product_product
    }
  }

  ${Product.fragments.product}
`;

export default function ProductsPage() {
  const [showDisabled, setShowDisabled] = React.useState(false);

  const { data, loading, refetch } = useQuery(productsQuery, {
    variables: { disabled: showDisabled ? undefined : false },
    fetchPolicy: "network-only",
  });

  return (
    <Container fixed>
      <Layout.Toolbar fixed>
        <Stack spacing={2} direction="row" alignItems="center">
          <CreateProductDialog onSuccess={refetch}>
            {({ open }) => (
              <Fab onClick={open} size="small" color="primary">
                <CreateIcon />
              </Fab>
            )}
          </CreateProductDialog>

          <Typography variant="h4" color="primary" sx={{ flexGrow: 1 }}>
            Produits
          </Typography>

          <div>
            <FormControl>
              <FormControlLabel
                label="Afficher les produits désactivés"
                control={
                  <Switch
                    checked={showDisabled}
                    onChange={(evt, checked) => setShowDisabled(checked)}
                  />
                }
              />
            </FormControl>
          </div>
        </Stack>
      </Layout.Toolbar>

      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nom</TableCell>
              <TableCell align="right">Prix (HT)</TableCell>
              <TableCell align="right">Code comptable</TableCell>
              <TableCell align="center">Statut</TableCell>
              <TableCell padding="checkbox" />
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan="5" align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            )}

            {data?.products.map((product) => (
              <ProductTableRow
                key={product.id}
                product={product}
                onUpdate={refetch}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
}
